/**
 * Meta Pixel + CAPI mirror — site-wide hook.
 *
 * - Loads fbevents.js only when:
 *   1. backend `/api/meta/pixel-config` returns enabled:true with a pixel_id, AND
 *   2. user has accepted analytics consent (localStorage `_en_meta_consent === 'granted'`).
 *
 * - Fires PageView automatically on every route change.
 * - Exposes `window.metaTrack(eventName, customData)` so any component can
 *   fire standard events (ViewContent, Search, Lead, AddToCart,
 *   InitiateCheckout, Purchase) which:
 *   1. Generate a unique event_id
 *   2. Fire the browser pixel call with that id
 *   3. POST a mirrored event to /api/meta/track so CAPI gets it server-side too.
 *      Meta dedupes browser+server using the shared event_id.
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CONSENT_KEY = '_en_meta_consent';

function _consentGranted() {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'granted';
  } catch {
    return false;
  }
}

function _eventId() {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 11)}`;
}

function _injectFbq(pixelId) {
  if (window.fbq) return;
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq('init', pixelId);
}

/** Public helper: track any custom Meta event (browser + CAPI mirror). */
export function metaTrack(eventName, customData = {}) {
  if (!_consentGranted()) return null;
  const eventId = _eventId();
  try {
    if (window.fbq) {
      window.fbq('track', eventName, customData, { eventID: eventId });
    }
  } catch {}
  // Mirror to backend CAPI (dedup via event_id)
  try {
    const body = JSON.stringify({
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      custom_data: customData,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/meta/track`, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(`${API}/meta/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {}
  return eventId;
}

if (typeof window !== 'undefined') {
  window.metaTrack = metaTrack;
}

/** Site-wide hook. Mount once at the App root. */
export default function useMetaPixel() {
  const { pathname } = useLocation();
  const initRef = useRef({ checked: false, enabled: false, pixelId: null });
  const lastPathRef = useRef(null);

  // Load config + initialize fbq once
  useEffect(() => {
    if (initRef.current.checked) return;
    initRef.current.checked = true;
    (async () => {
      if (!_consentGranted()) return;
      try {
        const res = await fetch(`${API}/meta/pixel-config`);
        if (!res.ok) return;
        const data = await res.json();
        if (!data.enabled || !data.pixel_id) return;
        initRef.current.enabled = true;
        initRef.current.pixelId = data.pixel_id;
        _injectFbq(data.pixel_id);
        // Fire the very first PageView once initialized
        metaTrack('PageView', { path: window.location.pathname });
      } catch {}
    })();
  }, []);

  // Fire PageView on subsequent route changes
  useEffect(() => {
    if (!pathname || lastPathRef.current === pathname) return;
    lastPathRef.current = pathname;
    if (!initRef.current.enabled) return;
    metaTrack('PageView', { path: pathname });
  }, [pathname]);
}
